import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import { LoadingPage } from '../loading-page';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import UsersTeamService from '../../../shared/services/users-teams/users-teams.service';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import LocationService from '../../../shared/services/location/location.service';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils';
import { ILocation } from '../../../shared/services/location/location.model';
import UserPermissionService from '../../../shared/services/user-permission/user-permission.service';
import { IUserPermissionSummary } from '../../../shared/services/user-permission/user-permission.model';
import MfaService from '../../../shared/services/mfa/mfa.service';
import { InviteUserDialog } from '../../dialogs/settings/users/invite-user-dialog';
import { IInviteUserDetails, IRole } from '../../../shared/services/users-teams/users-teams.model';
import { EditOutlined } from '@mui/icons-material';

type LocationState = {
  user?: IUserDetails;
};

function uniq(arr: string[]) {
  return Array.from(new Set(arr));
}

export const SettingsUserDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const userNotifications = useContext(UserNotificationsContext);

  const userDetailsService = useMemo(() => new UserDetailsService(), []);
  const usersTeamService = useMemo(() => new UsersTeamService(), []);
  const subsidiaryService = useMemo(() => SubsidiaryService.getInstance(), []);
  const locationService = useMemo(() => LocationService.getInstance(), []);
  const userPermissionService = useMemo(() => new UserPermissionService(), []);
  const mfaService = useMemo(() => new MfaService(), []);

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<IUserDetails | undefined>(state.user);
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [perm, setPerm] = useState<IUserPermissionSummary | undefined>();

  const [roles, setRoles] = useState<IRole[]>();
  const [openInviteDialog, setOpenInviteDialog] = useState<boolean>(false);
  const [isEditUserDialog, setIsEditUserDialog] = useState<boolean>(true);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [inviteUserDetails, setInviteUserDetails] = useState<IInviteUserDetails>({
    id: '',
    firstName: '',
    surname: '',
    email: '',
    roles: [],
  });

  const [isResettingMfa, setIsResettingMfa] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftAccessAllLocations, setDraftAccessAllLocations] = useState(true);
  const [draftAccessAllSubsidiaries, setDraftAccessAllSubsidiaries] = useState(true);
  const [draftLocationIds, setDraftLocationIds] = useState<string[]>([]);
  const [draftSubsidiaryIds, setDraftSubsidiaryIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const canEdit = true; // route is admin-only already

  const selectedLocations = useMemo(
    () => locations.filter((l) => draftLocationIds.includes(l.id)),
    [locations, draftLocationIds],
  );
  const selectedSubsidiaries = useMemo(
    () => subsidiaries.filter((s) => draftSubsidiaryIds.includes(s.id)),
    [subsidiaries, draftSubsidiaryIds],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!id) return;
      setIsLoading(true);
      try {
        const [allSubs, allLocs, permissions, allRoles] = await Promise.all([
          subsidiaryService.getAll(true),
          locationService.getAllLocations(true),
          userPermissionService.getByUser(id),
          usersTeamService.getAllRoles(),
        ]);

        if (cancelled) return;
        setSubsidiaries(allSubs || []);
        setLocations(allLocs || []);
        setPerm(permissions);
        setRoles(allRoles);

        // Best-effort user lookup (we also accept navigation state).
        if (!user) {
          // Try multiple statuses to find the user.
          const statuses = ['active', 'inactive', 'invited'];
          let found: IUserDetails | undefined;
          for (const status of statuses) {
            // eslint-disable-next-line no-await-in-loop
            const res = await userDetailsService.getAllUserDetailsWithPagination(status, 0, 1000);
            found = res.rows.find((u) => u.id === id);
            if (found) break;
          }
          if (found && !cancelled) setUser(found);
        }

        setDraftAccessAllLocations(!!permissions.accessAllLocations);
        setDraftAccessAllSubsidiaries(!!permissions.accessAllSubsidiaries);
        setDraftLocationIds(uniq(permissions.locationIds || []));
        setDraftSubsidiaryIds(uniq(permissions.subsidiaryIds || []));
      } catch (err) {
        console.log(err);
        userNotifications.openSnackbar({ open: true, message: 'Failed to load user permissions', backgroundColor: 'text.primary' });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
    // intentionally omit user from deps to avoid refetch loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshUser = async () => {
    if (!id) return;
    try {
      const statuses = ['active', 'inactive', 'invited'];
      let found: IUserDetails | undefined;
      for (const status of statuses) {
        // eslint-disable-next-line no-await-in-loop
        const res = await userDetailsService.getAllUserDetailsWithPagination(status, 0, 1000);
        found = res.rows.find((u) => u.id === id);
        if (found) break;
      }
      if (found) setUser(found);
    } catch (err) {
      console.log(err);
    }
  };

  // Admin lockout recovery: clears the user's two-factor setup (audit-logged
  // server-side). They re-enrol from their own profile afterwards.
  const resetMfa = async () => {
    if (!user) return;
    const confirmed = await userNotifications.openDialog({
      active: true,
      title: 'Reset two-factor authentication?',
      message: `This turns off two-factor authentication for ${user.firstName} ${user.surname}. Use it when they are locked out; they can set it up again from their own profile.`,
      buttonCopy: 'Reset',
      cancelButtonCopy: 'Cancel',
    });
    if (!confirmed) return;
    setIsResettingMfa(true);
    try {
      await mfaService.adminReset(user.id);
      userNotifications.openSnackbar({ open: true, message: 'Two-factor authentication reset' });
    } catch (err: any) {
      userNotifications.openSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to reset two-factor authentication', backgroundColor: 'text.primary' });
    } finally {
      setIsResettingMfa(false);
    }
  };

  const openEditUserDialog = () => {
    if (!user) return;
    setInviteUserDetails({
      id: user.id,
      firstName: user.firstName,
      surname: user.surname,
      email: user.email,
      roles: user.roles || [],
    });
    setSelectedRoles(user.roles || []);
    setIsEditUserDialog(true);
    setOpenInviteDialog(true);
  };

  const closeInviteDialogHandler = () => {
    setOpenInviteDialog(false);
    setInviteUserDetails({
      id: '',
      firstName: '',
      surname: '',
      email: '',
      roles: [],
    });
    setSelectedRoles([]);
    setIsEditUserDialog(true);
    // refresh after close in case it was saved
    refreshUser();
  };

  const resetDraftFromPerm = () => {
    if (!perm) return;
    setDraftAccessAllLocations(!!perm.accessAllLocations);
    setDraftAccessAllSubsidiaries(!!perm.accessAllSubsidiaries);
    setDraftLocationIds(uniq(perm.locationIds || []));
    setDraftSubsidiaryIds(uniq(perm.subsidiaryIds || []));
  };

  const onCancelEdit = () => {
    resetDraftFromPerm();
    setIsEditing(false);
  };

  const onSave = async () => {
    if (!id || !perm) return;
    setIsSaving(true);
    try {
      // 1) flags
      const flagsChanged =
        draftAccessAllLocations !== perm.accessAllLocations ||
        draftAccessAllSubsidiaries !== perm.accessAllSubsidiaries;

      // 2) perms diff
      const nextLocationIds = uniq(draftLocationIds);
      const nextSubsidiaryIds = uniq(draftSubsidiaryIds);

      const prevLocationIds = uniq(perm.locationIds || []);
      const prevSubsidiaryIds = uniq(perm.subsidiaryIds || []);

      const addLocationIds = nextLocationIds.filter((x) => !prevLocationIds.includes(x));
      const removeLocationIds = prevLocationIds.filter((x) => !nextLocationIds.includes(x));
      const addSubsidiaryIds = nextSubsidiaryIds.filter((x) => !prevSubsidiaryIds.includes(x));
      const removeSubsidiaryIds = prevSubsidiaryIds.filter((x) => !nextSubsidiaryIds.includes(x));

      if (flagsChanged) {
        await userPermissionService.updateFlags({
          userId: id,
          accessAllLocations: draftAccessAllLocations,
          accessAllSubsidiaries: draftAccessAllSubsidiaries,
        });
      }

      await Promise.all([
        ...addLocationIds.map((locationId) => userPermissionService.addLocation({ userId: id, locationId })),
        ...removeLocationIds.map((locationId) => userPermissionService.removeLocation({ userId: id, locationId })),
        ...addSubsidiaryIds.map((subsidiaryId) => userPermissionService.addSubsidiary({ userId: id, subsidiaryId })),
        ...removeSubsidiaryIds.map((subsidiaryId) => userPermissionService.removeSubsidiary({ userId: id, subsidiaryId })),
      ]);

      const refreshed = await userPermissionService.getByUser(id);
      setPerm(refreshed);
      setIsEditing(false);
      userNotifications.openSnackbar({ open: true, message: 'Permissions saved' });
    } catch (err) {
      console.log(err);
      userNotifications.openSnackbar({ open: true, message: 'Failed to save permissions', backgroundColor: 'text.primary' });
    } finally {
      setIsSaving(false);
    }
  };

  const title = user ? `${user.firstName} ${user.surname}`.trim() : 'User';

  return (
    <PageOuterContainer>
      <PageHeading
        includeBackButton={true}
        backButtonLink='/settings/company-settings/users'
        backButtonText='Back to users'
        title={title}
        hasTabs={false}
      />

      <LoadingPage pageReady={!isLoading}>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>User details</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {user?.email || '—'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {(user?.roles || []).map((r) => (
                      <Chip key={r} label={r.charAt(0).toUpperCase() + r.slice(1)} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={resetMfa}
                    disabled={!user || isResettingMfa}
                  >
                    Reset 2FA
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlined />}
                    onClick={openEditUserDialog}
                    disabled={!user}
                  >
                    Edit
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Entity permissions</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Controls which subsidiaries and locations this user can see and enter data for.
                  </Typography>
                  {perm && !perm.accessAllLocations && (perm.locationIds || []).length === 0 &&
                    <Chip size='small' variant='outlined' color='warning' sx={{ mt: 1 }}
                      label='No locations assigned: this user will see no data until some are added' />
                  }
                </Box>

                <Stack direction="row" spacing={1}>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlined />}
                      disabled={!canEdit}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button variant="text" onClick={onCancelEdit} disabled={isSaving}>Cancel</Button>
                      <Button variant="contained" onClick={onSave} disabled={isSaving}>
                        Save
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2} sx={{ opacity: isEditing ? 1 : 0.65, pointerEvents: isEditing ? 'auto' : 'none' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControlLabel
                    control={<Checkbox checked={draftAccessAllLocations} onChange={(e) => setDraftAccessAllLocations(e.target.checked)} />}
                    label="Access all locations"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={draftAccessAllSubsidiaries} onChange={(e) => setDraftAccessAllSubsidiaries(e.target.checked)} />}
                    label="Access all subsidiaries"
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={getSelectableSubsidiaries(subsidiaries, draftSubsidiaryIds)}
                    getOptionLabel={(o: ISubsidiary) => o?.name || ''}
                    value={selectedSubsidiaries}
                    onChange={(_, value) => setDraftSubsidiaryIds(uniq(value.map((v) => v.id)))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed subsidiaries"
                        placeholder={draftAccessAllSubsidiaries ? 'All subsidiaries (toggle off to restrict)' : 'Select subsidiaries'}
                      />
                    )}
                    sx={{ flex: 1 }}
                    disabled={draftAccessAllSubsidiaries}
                  />

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={locations}
                    getOptionLabel={(o: ILocation) => o?.friendlyName || ''}
                    value={selectedLocations}
                    onChange={(_, value) => setDraftLocationIds(uniq(value.map((v) => v.id)))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed locations"
                        placeholder={draftAccessAllLocations ? 'All locations (toggle off to restrict)' : 'Select locations'}
                      />
                    )}
                    sx={{ flex: 1 }}
                    disabled={draftAccessAllLocations}
                  />
                </Stack>
              </Stack>

              {!isEditing && perm && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Current restrictions</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Chip
                      label={perm.accessAllSubsidiaries ? 'Subsidiaries: All' : `Subsidiaries: ${perm.subsidiaryIds.length}`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={perm.accessAllLocations ? 'Locations: All' : `Locations: ${perm.locationIds.length}`}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </LoadingPage>

      {roles && openInviteDialog && (
        <InviteUserDialog
          roles={roles}
          open={openInviteDialog}
          closeHandler={closeInviteDialogHandler}
          dialogDetails={inviteUserDetails}
          setSelectedRoles={setSelectedRoles}
          selectedRoles={selectedRoles}
          getUsers={refreshUser}
          isEdit={isEditUserDialog}
        />
      )}
    </PageOuterContainer>
  );
};

